import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from 'src/global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  createCompanyCheckinRecord as createCompanyCheckinRecordAPI,
  createCompanyHeadsetsNumberRecord as createCompanyHeadsetsNumberRecordAPI,
  deleteCompanyCheckinRecord as deleteCompanyCheckinRecordAPI,
  deleteCompanyHeadsetsNumberRecord as deleteCompanyHeadsetsNumberRecordAPI,
  getCompanyParameters,
  updateCompanyCheckinRecord as updateCompanyCheckinRecordAPI,
  updateCompanyHeadsetsNumberRecord as updateCompanyHeadsetsNumberRecordAPI,
} from 'src/features/companyParameters/companyParameters.api';
import { CompanyParameters } from 'src/features/companyParameters/companyParameters.types';

const name = 'companyParameters';

const companyParametersEntityAdapter = createEntityAdapter<AsyncEntity<CompanyParameters>>({
  selectId: item => item.companyId,
});

export const companyParametersSelectors = companyParametersEntityAdapter.getSelectors();

export const fetchCompanyParameters = createAsyncThunkFromFetch(`${name}/fetchCompanyParameters`, getCompanyParameters);
export const createCompanyCheckinRecord = createAsyncThunkFromFetch(
  `${name}/createCompanyCheckinRecord`,
  createCompanyCheckinRecordAPI
);
export const createCompanyWorkingHeadsetsNumberRecord = createAsyncThunkFromFetch(
  `${name}/createCompanyHeadsetsNumberRecord`,
  createCompanyHeadsetsNumberRecordAPI
);
export const updateCompanyCheckinRecord = createAsyncThunkFromFetch(
  `${name}/updateCompanyCheckinRecord`,
  updateCompanyCheckinRecordAPI
);
export const updateCompanyHeadsetsNumberRecord = createAsyncThunkFromFetch(
  `${name}/updateCompanyHeadsetsNumberRecord`,
  updateCompanyHeadsetsNumberRecordAPI
);
export const deleteCompanyCheckinRecord = createAsyncThunkFromFetch(
  `${name}/deleteCompanyCheckinRecord`,
  deleteCompanyCheckinRecordAPI
);
export const deleteCompanyDevicesNumberRecord = createAsyncThunkFromFetch(
  `${name}/deleteCompanyHeadsetsNumberRecord`,
  deleteCompanyHeadsetsNumberRecordAPI
);

const companyParametersSlice = createSlice({
  name,
  initialState: companyParametersEntityAdapter.getInitialState<{
    isPending: boolean;
    hasError: boolean;
    companySelector: Record<string, string[]>;
  }>({
    isPending: false,
    hasError: false,
    companySelector: {},
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchCompanyParameters.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchCompanyParameters.fulfilled, (state, { meta, payload }) => {
      companyParametersEntityAdapter.setOne(state, {
        companyId: meta.arg.companyId,
        checkins: payload.checkins,
        devices: payload.devices,
      });
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchCompanyParameters.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(createCompanyCheckinRecord.fulfilled, (state, { meta, payload }) => {
      const companyId = meta.arg.companyId;
      const params = companyParametersSelectors.selectById(state, companyId) as CompanyParameters;
      companyParametersEntityAdapter.updateOne(state, {
        id: companyId,
        changes: {
          checkins: [...params.checkins, payload],
        },
      });
    });
    builder.addCase(createCompanyWorkingHeadsetsNumberRecord.fulfilled, (state, { meta, payload }) => {
      const companyId = meta.arg.companyId;
      const params = companyParametersSelectors.selectById(state, companyId) as CompanyParameters;
      companyParametersEntityAdapter.updateOne(state, {
        id: companyId,
        changes: {
          devices: [...params.devices, payload],
        },
      });
    });
    builder.addCase(deleteCompanyCheckinRecord.fulfilled, (state, { meta }) => {
      const companyId = meta.arg.companyId;
      const params = companyParametersSelectors.selectById(state, companyId) as CompanyParameters;
      companyParametersEntityAdapter.updateOne(state, {
        id: companyId,
        changes: {
          checkins: params.checkins.filter(c => c.id !== meta.arg.recordId),
        },
      });
    });
    builder.addCase(deleteCompanyDevicesNumberRecord.fulfilled, (state, { meta }) => {
      const companyId = meta.arg.companyId;
      const params = companyParametersSelectors.selectById(state, companyId) as CompanyParameters;
      companyParametersEntityAdapter.updateOne(state, {
        id: companyId,
        changes: {
          devices: params.devices.filter(c => c.id !== meta.arg.recordId),
        },
      });
    });
  },
});

const { reducer } = companyParametersSlice;

export default reducer;
